import { useCallback } from "react";
import { useSelector } from "react-redux";
import useHighlightLoad from "./useHighlightsLoad";
import { getHighlightsData } from "../../api/features/highlights/selectors";
import useMarkers from "../markers/useMarkers";
import {
  deleteHighlight,
  goToHighlight,
  highlightCurrentSelection,
  updateHighlight,
} from "../../api/features/highlights/actions";
import { UpdateHighlightData } from "../../api/features/highlights/types";
import { useAppDispatch } from "../../api/core/useAppDispatch";
import { combineDetailedStatuses } from "../../../../../common/utils/async-status";
import { getHighlightsSubscribedMarkers } from "../../api/features/markers/selectors";
import { apiMetaData } from "../../api/core/ViewerApiMetaData";
import useBookInfo from "../bookinfo/useBookInfo";
import { VBKType } from "../../api/features/bookinfo/types";
import { getStartCfiFromRange } from "../../../../../common/utils/cfi";

export default function useHighlights() {
  const highlightsLoadStatus = useHighlightLoad();
  const { loadStatus: markerLoadStatus } = useMarkers();
  const highlightsData = useSelector(getHighlightsData);
  const highlightsSubscribedMarkers = useSelector(
    getHighlightsSubscribedMarkers
  );
  const { bookInfo } = useBookInfo();
  const dispatch = useAppDispatch();

  const dispatchGoToHighlight = useCallback(
    (syncId: string, subscribed: boolean) => {
      // cfi metadata needed for back to page as syncId might get deleted mid flow
      const highlight = highlightsData?.find(
        (highlight) => highlight.syncId === syncId
      );
      let highlightCfi;
      if (bookInfo.vbkType === VBKType.EPUB) {
        const startCfi = getStartCfiFromRange(highlight?.cfi ?? "");
        highlightCfi = startCfi ? startCfi.toString() : null;
      } else {
        highlightCfi = highlight?.location
          ? "/" + highlight.location.split("/").pop()
          : null;
      }
      return dispatch(
        goToHighlight({ syncId, subscribed }, apiMetaData({ highlightCfi }))
      );
    },
    [bookInfo, dispatch, highlightsData]
  );

  const dispatchHighlightCurrentSelection = useCallback(
    (markerId: string, text?: string) => {
      return dispatch(highlightCurrentSelection({ markerId, text }));
    },
    [dispatch]
  );

  const dispatchDeleteHighlight = useCallback(
    (syncId: string) => {
      return dispatch(deleteHighlight({ syncId }));
    },
    [dispatch]
  );

  const dispatchUpdateHighlight = useCallback(
    (syncId: string, data?: UpdateHighlightData) => {
      return dispatch(updateHighlight({ syncId, dataFields: data }));
    },
    [dispatch]
  );

  return {
    loadStatus: combineDetailedStatuses([
      highlightsLoadStatus,
      markerLoadStatus,
    ]),
    highlightsData,
    numHighlights: highlightsData?.length ?? 0,
    highlightsSubscribedMarkers,
    goToHighlight: dispatchGoToHighlight,
    highlightCurrentSelection: dispatchHighlightCurrentSelection,
    deleteHighlight: dispatchDeleteHighlight,
    updateHighlight: dispatchUpdateHighlight,
  };
}
